import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  User,
  Shield,
  Plane,
  Car,
  HeartPulse,
  Briefcase,
  GraduationCap,
  Users,
  Home,
  Sparkles,
  Star,
  Network,
  Folder,
  Tag,
  Trash2,
  Plus,
  Search,
  LayoutGrid,
  List as ListIcon,
  RefreshCw,
  MoreVertical,
  Edit3,
  Copy,
  Share2,
  History,
  Lock,
  Paperclip,
  RotateCcw,
  Check,
  Eye,
  EyeOff,
  Clock,
  ArrowUpDown,
  Filter,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Download,
  Calendar,
  ExternalLink,
  X,
} from 'lucide-react';
import { MainLayout } from '../../components/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useApplications } from '../../hooks/useApplications';
import { VaultCrypto } from '@password-manager/crypto';
import { WebCryptoProvider } from '../../crypto/WebCryptoProvider';
import api from '../../api/client';
import { copyTextToClipboard } from '../../utils/clipboard';

// Modals
import IdentityTemplatesModal, { IDENTITY_TEMPLATES } from '../../components/identities/IdentityTemplatesModal';
import IdentityEditorModal from '../../components/identities/IdentityEditorModal';
import IdentityViewModal from '../../components/identities/IdentityViewModal';
import IdentityActivityModal from '../../components/identities/IdentityActivityModal';
import {
  MoveIdentityCategoryModal,
  ManageIdentityTagsModal,
  DeleteIdentityModal,
} from '../../components/identities/IdentityActionModals';
import { ShareVaultItemModal } from '../../components/ShareVaultItemModal';

const generateUuid = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const IDENTITY_TYPE_ICONS = {
  personal: User,
  government_id: Shield,
  passport: Plane,
  driving_licence: Car,
  health_id: HeartPulse,
  work_id: Briefcase,
  student_id: GraduationCap,
  family_member: Users,
  address_id: Home,
  custom: Sparkles,
};

const IDENTITY_TYPE_NAMES = {
  personal: 'Personal Identity',
  government_id: 'Government ID / SSN',
  passport: 'Passport',
  driving_licence: 'Driving Licence',
  health_id: 'Health & Insurance ID',
  work_id: 'Work Identity',
  student_id: 'Student ID',
  family_member: 'Family Member',
  address_id: 'Address Identity',
  custom: 'Custom Identity',
};

const DEFAULT_CATEGORIES = [
  { id: 'cat-personal', name: 'Personal', icon: '👤' },
  { id: 'cat-family', name: 'Family', icon: '👨‍👩‍👧‍👦' },
  { id: 'cat-work', name: 'Work', icon: '💼' },
  { id: 'cat-legal', name: 'Legal', icon: '⚖️' },
  { id: 'cat-medical', name: 'Medical', icon: '🩺' },
];

export default function IdentitiesApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { vaultKey, user } = useAuth();
  const { hasPermission } = useApplications();

  // URL query params: all | favorites | personal | family | work | shared-with-me | shared-by-me | documents | categories | trash
  const searchParams = new URLSearchParams(location.search);
  const activeFilter = searchParams.get('filter') || 'all';
  const urlCategory = searchParams.get('category') || '';
  const urlTag = searchParams.get('tag') || '';
  const identityIdParam = searchParams.get('identityId');

  const vaultCrypto = useMemo(() => new VaultCrypto(WebCryptoProvider), []);

  // State
  const [vaultId, setVaultId] = useState(null);
  const [identities, setIdentities] = useState([]);
  const [categories, setCategories] = useState(() => {
    const saved = localStorage.getItem('ciphervault_identity_categories');
    if (saved) {
      try { return JSON.parse(saved); } catch (e) {}
    }
    return DEFAULT_CATEGORIES;
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  // Filtering, Search & Layout controls
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState(urlCategory || 'all');
  const [expiryFilter, setExpiryFilter] = useState('all'); // 'all' | 'valid' | 'expiring_soon' | 'expired'
  const [sortBy, setSortBy] = useState('updated_desc'); // 'updated_desc' | 'title_asc' | 'title_desc' | 'expiry_soonest' | 'created_desc'
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('ciphervault_identities_view') || 'grid';
  });
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  // Sensitive number reveal state on cards (map of id -> boolean)
  const [revealedIds, setRevealedIds] = useState({});
  const cardHideTimersRef = useRef({});

  // Active modal targets
  const [viewingIdentity, setViewingIdentity] = useState(null);
  const [editingIdentity, setEditingIdentity] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [activityIdentity, setActivityIdentity] = useState(null);
  const [shareIdentityTarget, setShareIdentityTarget] = useState(null);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [moveToCategoryTarget, setMoveToCategoryTarget] = useState(null);
  const [manageTagsTarget, setManageTagsTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeMenuId, setActiveMenuId] = useState(null);

  // Copied state indicator
  const [copiedId, setCopiedId] = useState(null);

  // Permissions
  const canCreate = hasPermission('identities', 'create') ?? true;
  const canEdit = hasPermission('identities', 'edit') ?? true;
  const canDelete = hasPermission('identities', 'delete') ?? true;
  const canShare = hasPermission('identities', 'share') ?? true;

  const showToast = useCallback((msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  }, []);

  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('ciphervault_identities_view', mode);
  };

  // Close menus on outside click
  useEffect(() => {
    const handleClick = () => setActiveMenuId(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  // Cleanup auto-hide timers
  useEffect(() => {
    return () => {
      Object.values(cardHideTimersRef.current).forEach((t) => clearTimeout(t));
    };
  }, []);

  // Toggle card-level sensitive number reveal with 15s auto-hide
  const toggleRevealCard = (identityId) => {
    setRevealedIds((prev) => {
      const current = Boolean(prev[identityId]);
      if (cardHideTimersRef.current[identityId]) {
        clearTimeout(cardHideTimersRef.current[identityId]);
      }

      if (!current) {
        // Turning on -> set 15s timer
        cardHideTimersRef.current[identityId] = setTimeout(() => {
          setRevealedIds((p) => ({ ...p, [identityId]: false }));
        }, 15000);
        return { ...prev, [identityId]: true };
      } else {
        return { ...prev, [identityId]: false };
      }
    });
  };

  // ─────────────────────────────────────────────
  // Load & Decrypt Identities
  // ─────────────────────────────────────────────
  const loadIdentities = useCallback(async () => {
    if (!vaultKey) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');

    try {
      // 1. Fetch user vaults
      const vaultsRes = await api.get('/vaults');
      const v = vaultsRes.data?.[0];
      if (!v) {
        setLoading(false);
        return;
      }
      setVaultId(v.id);

      // 2. Fetch vault items
      const itemsRes = await api.get(`/vaults/${v.id}/items`);
      const allItems = itemsRes.data || [];

      // Filter only IDENTITY items
      const rawIdentityItems = allItems.filter(
        (item) => item.item_type === 'IDENTITY' || item.item_type === 'identity'
      );

      // Decrypt client-side
      const decrypted = await Promise.all(
        rawIdentityItems.map(async (item) => {
          try {
            const jsonText = await vaultCrypto.decryptItem(
              item.encrypted_data,
              vaultKey,
              item.id
            );
            const parsed = JSON.parse(jsonText);
            return {
              id: item.id,
              vault_id: item.vault_id,
              status: item.status || 'ACTIVE',
              is_favorite: Boolean(item.is_favorite),
              category_id: item.category_id,
              created_at: item.created_at,
              updated_at: item.updated_at,
              ...parsed,
            };
          } catch (decryptErr) {
            console.error(`Failed to decrypt identity ${item.id}:`, decryptErr);
            return {
              id: item.id,
              vault_id: item.vault_id,
              title: 'Decryption Error',
              status: item.status || 'ACTIVE',
              is_favorite: Boolean(item.is_favorite),
              decryptionFailed: true,
              fields: {},
            };
          }
        })
      );

        const normalized = await Promise.all(
          decrypted.map(async (item) => {
            const isUuid =
              typeof item.id === 'string' &&
              /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(item.id);
            if (!isUuid) {
              const newValidId = generateUuid();
              const upgraded = {
                ...item,
                id: newValidId,
                is_favorite: Boolean(item.is_favorite || item.isFavorite),
              };
              try {
                const encryptedData = await vaultCrypto.encryptItem(
                  JSON.stringify(upgraded),
                  vaultKey,
                  newValidId
                );
                await api.post(`/vaults/${v.id}/items`, {
                  id: newValidId,
                  itemType: 'IDENTITY',
                  encryptedData,
                  isFavorite: upgraded.is_favorite,
                  status: upgraded.status || 'ACTIVE',
                });
                return upgraded;
              } catch (e) {
                return upgraded;
              }
            }
            return item;
          })
        );
        setIdentities(normalized);
    } catch (err) {
      console.error('Failed to load identities:', err);
      setLoadError('Failed to load encrypted identities. Please verify your connection.');
    } finally {
      setLoading(false);
    }
  }, [vaultKey, vaultCrypto]);

  useEffect(() => {
    loadIdentities();
  }, [loadIdentities]);

  // Handle identityId query param if present
  useEffect(() => {
    if (identityIdParam && identities.length > 0) {
      const match = identities.find((i) => i.id === identityIdParam);
      if (match) setViewingIdentity(match);
    }
  }, [identityIdParam, identities]);

  // Sync category filter
  useEffect(() => {
    if (urlCategory) setCategoryFilter(urlCategory);
  }, [urlCategory]);

  // ─────────────────────────────────────────────
  // CRUD Actions
  // ─────────────────────────────────────────────

  // Save Identity (Create or Update)
  const handleSaveIdentity = async (formData) => {
    if (!vaultId || !vaultKey) {
      showToast('Vault credentials not available. Please unlock your vault.');
      return;
    }

    const isUpdating = Boolean(editingIdentity?.id);
    const isTargetUuid =
      isUpdating &&
      typeof editingIdentity.id === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        editingIdentity.id
      );
    const identityId = isTargetUuid ? editingIdentity.id : generateUuid();
    const now = new Date().toISOString();

    const previousVersions = editingIdentity?.versions || [];
    const newVersionNumber = previousVersions.length + 1;

    const currentVersionRecord = {
      version: newVersionNumber,
      savedAt: now,
      title: formData.title,
      summary: isUpdating ? 'Updated identity details' : 'Initial identity record created',
      fields: {
        fullName: formData.fields?.fullName || '',
        documentNumber: formData.fields?.documentNumber || '',
        expiryDate: formData.fields?.expiryDate || '',
      },
    };

    const newAuditEntry = {
      action: isUpdating ? 'Updated' : 'Created',
      actor: 'You (Current Device)',
      timestamp: now,
      details: isUpdating
        ? `Version ${newVersionNumber} saved with updated encrypted fields`
        : 'Zero-knowledge identity record registered',
    };

    const fullIdentityObj = {
      id: identityId,
      title: formData.title,
      identityType: formData.identityType,
      category: formData.category,
      tags: formData.tags || [],
      fields: formData.fields || {},
      attachments: formData.attachments || [],
      is_favorite: Boolean(formData.is_favorite ?? editingIdentity?.is_favorite),
      isFavorite: Boolean(formData.is_favorite ?? editingIdentity?.is_favorite),
      isLocked: Boolean(formData.isLocked),
      lockPin: formData.lockPin || '',
      status: editingIdentity?.status || 'ACTIVE',
      created_at: editingIdentity?.created_at || now,
      updated_at: now,
      versions: [currentVersionRecord, ...previousVersions],
      auditLog: [newAuditEntry, ...(editingIdentity?.auditLog || [])],
    };

    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(fullIdentityObj),
        vaultKey,
        identityId
      );

      let saved = false;
      if (isTargetUuid) {
        try {
          await api.put(`/vaults/${vaultId}/items/${identityId}`, {
            id: identityId,
            itemType: 'IDENTITY',
            encryptedData,
            isFavorite: fullIdentityObj.is_favorite,
            status: fullIdentityObj.status,
          });
          saved = true;
        } catch (putErr) {
          if (putErr.response?.status !== 404 && putErr.response?.status !== 400) {
            throw putErr;
          }
        }
      }

      if (!saved) {
        await api.post(`/vaults/${vaultId}/items`, {
          id: identityId,
          itemType: 'IDENTITY',
          encryptedData,
          isFavorite: fullIdentityObj.is_favorite,
          status: fullIdentityObj.status,
        });
      }

      if (isUpdating) {
        setIdentities((prev) =>
          prev.map((i) =>
            i.id === editingIdentity.id || i.id === identityId
              ? fullIdentityObj
              : i
          )
        );
        if (
          viewingIdentity?.id === editingIdentity.id ||
          viewingIdentity?.id === identityId
        ) {
          setViewingIdentity(fullIdentityObj);
        }
        showToast('Identity updated successfully');
      } else {
        setIdentities((prev) => [fullIdentityObj, ...prev]);
        showToast('New identity securely created');
      }

      setIsEditorOpen(false);
      setEditingIdentity(null);
    } catch (err) {
      console.error('Failed to save identity:', err);
      showToast(isUpdating ? 'Failed to update identity' : 'Failed to create identity');
    }
  };

  // Toggle Favorite
  const handleToggleFavorite = async (target) => {
    if (!vaultId || !vaultKey) return;
    const currentFav = Boolean(target.is_favorite || target.isFavorite);
    const newFav = !currentFav;
    let itemId = target.id;
    const isTargetUuid =
      typeof itemId === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(itemId);
    if (!isTargetUuid) {
      itemId = generateUuid();
    }
    const updated = {
      ...target,
      id: itemId,
      is_favorite: newFav,
      isFavorite: newFav,
      updated_at: new Date().toISOString(),
    };

    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        itemId
      );

      let saved = false;
      if (isTargetUuid) {
        try {
          await api.put(`/vaults/${vaultId}/items/${itemId}`, {
            id: itemId,
            itemType: 'IDENTITY',
            encryptedData,
            isFavorite: newFav,
            status: target.status || 'ACTIVE',
          });
          saved = true;
        } catch (putErr) {
          if (putErr.response?.status !== 404 && putErr.response?.status !== 400) {
            throw putErr;
          }
        }
      }

      if (!saved) {
        await api.post(`/vaults/${vaultId}/items`, {
          id: itemId,
          itemType: 'IDENTITY',
          encryptedData,
          isFavorite: newFav,
          status: target.status || 'ACTIVE',
        });
      }

      setIdentities((prev) =>
        prev.map((i) => (i.id === target.id || i.id === itemId ? updated : i))
      );
      if (viewingIdentity?.id === target.id || viewingIdentity?.id === itemId) {
        setViewingIdentity(updated);
      }
      showToast(newFav ? 'Added to Favorites' : 'Removed from Favorites');
    } catch (err) {
      console.error('Favorite update error:', err);
      showToast('Failed to update favorite status');
    }
  };

  // Duplicate Identity
  const handleDuplicateIdentity = async (target) => {
    if (!canCreate || !vaultId || !vaultKey) return;
    try {
      const newId = generateUuid();
      const now = new Date().toISOString();
      const duplicated = {
        ...target,
        id: newId,
        title: `[Copy] ${target.title || 'Untitled Identity'}`,
        is_favorite: false,
        status: 'ACTIVE',
        created_at: now,
        updated_at: now,
        versions: [
          {
            version: 1,
            savedAt: now,
            title: `[Copy] ${target.title || 'Untitled Identity'}`,
            summary: 'Duplicated from existing identity',
            fields: { ...target.fields },
          },
        ],
        auditLog: [
          {
            action: 'Created',
            actor: 'You (Current Device)',
            timestamp: now,
            details: `Cloned from identity "${target.title}"`,
          },
        ],
      };

      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(duplicated),
        vaultKey,
        newId
      );
      await api.post(`/vaults/${vaultId}/items`, {
        id: newId,
        itemType: 'IDENTITY',
        encryptedData,
      });
      setIdentities((prev) => [duplicated, ...prev]);
      showToast('Identity duplicated successfully');
    } catch (err) {
      showToast('Failed to duplicate identity');
    }
  };

  // Move to Category
  const handleMoveToCategory = async (target, newCategory) => {
    if (!vaultId || !vaultKey) return;
    const updated = {
      ...target,
      category: newCategory,
      updated_at: new Date().toISOString(),
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        target.id
      );
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'IDENTITY',
        encryptedData,
        isFavorite: target.is_favorite,
        status: target.status,
      });
      setIdentities((prev) => prev.map((i) => (i.id === target.id ? updated : i)));
      if (viewingIdentity?.id === target.id) setViewingIdentity(updated);
      showToast(`Moved to category "${newCategory}"`);
    } catch (err) {
      showToast('Failed to update category');
    }
  };

  // Manage Tags
  const handleSaveTags = async (target, newTags) => {
    if (!vaultId || !vaultKey) return;
    const updated = {
      ...target,
      tags: newTags,
      updated_at: new Date().toISOString(),
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        target.id
      );
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'IDENTITY',
        encryptedData,
        isFavorite: target.is_favorite,
        status: target.status,
      });
      setIdentities((prev) => prev.map((i) => (i.id === target.id ? updated : i)));
      if (viewingIdentity?.id === target.id) setViewingIdentity(updated);
      showToast('Tags updated successfully');
    } catch (err) {
      showToast('Failed to update tags');
    }
  };

  // Move to Trash (Soft Delete)
  const handleMoveToTrash = async (target) => {
    if (!canDelete || !vaultId || !vaultKey) return;
    const updated = {
      ...target,
      status: 'TRASHED',
      trashed_at: new Date().toISOString(),
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        target.id
      );
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'IDENTITY',
        encryptedData,
        isFavorite: target.is_favorite,
        status: 'TRASHED',
      });
      setIdentities((prev) => prev.map((i) => (i.id === target.id ? updated : i)));
      if (viewingIdentity?.id === target.id) setViewingIdentity(null);
      showToast('Identity moved to Trash');
    } catch (err) {
      showToast('Failed to move identity to Trash');
    }
  };

  // Restore from Trash
  const handleRestoreFromTrash = async (target) => {
    if (!vaultId || !vaultKey) return;
    const updated = {
      ...target,
      status: 'ACTIVE',
      trashed_at: null,
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        target.id
      );
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'IDENTITY',
        encryptedData,
        isFavorite: target.is_favorite,
        status: 'ACTIVE',
      });
      setIdentities((prev) => prev.map((i) => (i.id === target.id ? updated : i)));
      showToast('Identity restored to active workspace');
    } catch (err) {
      showToast('Failed to restore identity');
    }
  };

  // Permanent Delete
  const handlePermanentDelete = async (target) => {
    if (!canDelete || !vaultId) return;
    try {
      await api.delete(`/vaults/${vaultId}/items/${target.id}`);
      setIdentities((prev) => prev.filter((i) => i.id !== target.id));
      if (viewingIdentity?.id === target.id) setViewingIdentity(null);
      showToast('Identity permanently erased');
    } catch (err) {
      showToast('Failed to permanently delete identity');
    }
  };

  // Empty Trash
  const handleEmptyTrash = async () => {
    if (!canDelete || !vaultId) return;
    const trashed = identities.filter((i) => i.status === 'TRASHED');
    if (trashed.length === 0) return;
    try {
      await Promise.all(
        trashed.map((i) => api.delete(`/vaults/${vaultId}/items/${i.id}`))
      );
      setIdentities((prev) => prev.filter((i) => i.status !== 'TRASHED'));
      showToast('Trash emptied successfully');
    } catch (err) {
      showToast('Failed to empty trash');
    }
  };

  // Copy Link
  const handleCopyLink = (target) => {
    const url = `${window.location.origin}/apps/identities?identityId=${target.id}`;
    copyTextToClipboard(url);
    showToast('Identity link copied to clipboard');
  };

  // Rollback version
  const handleRestoreVersion = async (target, versionObj) => {
    if (!vaultId || !vaultKey) return;
    const now = new Date().toISOString();
    const updated = {
      ...target,
      title: versionObj.title || target.title,
      fields: {
        ...(target.fields || {}),
        ...(versionObj.fields || {}),
      },
      updated_at: now,
      auditLog: [
        {
          action: 'Restored',
          actor: 'You',
          timestamp: now,
          details: `Restored version snapshot v${versionObj.version}`,
        },
        ...(target.auditLog || []),
      ],
    };
    try {
      const encryptedData = await vaultCrypto.encryptItem(
        JSON.stringify(updated),
        vaultKey,
        target.id
      );
      await api.put(`/vaults/${vaultId}/items/${target.id}`, {
        id: target.id,
        itemType: 'IDENTITY',
        encryptedData,
        isFavorite: target.is_favorite,
        status: target.status,
      });
      setIdentities((prev) => prev.map((i) => (i.id === target.id ? updated : i)));
      if (viewingIdentity?.id === target.id) setViewingIdentity(updated);
      showToast(`Restored version v${versionObj.version}`);
    } catch (err) {
      showToast('Failed to restore version');
    }
  };

  // ─────────────────────────────────────────────
  // Expiry Calculations & Filtering
  // ─────────────────────────────────────────────
  const getExpiryDetails = (expiryDateStr) => {
    if (!expiryDateStr) return { status: 'none', label: 'No Expiry', daysRemaining: Infinity };
    const now = new Date();
    const expiry = new Date(expiryDateStr);
    if (isNaN(expiry.getTime())) return { status: 'none', label: 'Invalid Date', daysRemaining: Infinity };

    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { status: 'expired', label: `Expired (${Math.abs(diffDays)}d ago)`, color: '#ef4444', daysRemaining: diffDays };
    }
    if (diffDays <= 30) {
      return { status: 'expiring_soon', label: `Expiring Soon (${diffDays}d)`, color: '#f59e0b', daysRemaining: diffDays };
    }
    if (diffDays <= 90) {
      return { status: 'expiring_upcoming', label: `Expiring in ${diffDays}d`, color: '#3b82f6', daysRemaining: diffDays };
    }
    return { status: 'valid', label: `Valid until ${expiry.toLocaleDateString()}`, color: '#10b981', daysRemaining: diffDays };
  };

  // Expiry stats across all active identities
  const expiryStats = useMemo(() => {
    let expiredCount = 0;
    let expiringSoonCount = 0;

    identities.forEach((i) => {
      if (i.status === 'TRASHED') return;
      const f = i.fields || i;
      if (f.expiryDate) {
        const info = getExpiryDetails(f.expiryDate);
        if (info.status === 'expired') expiredCount++;
        else if (info.status === 'expiring_soon') expiringSoonCount++;
      }
    });

    return { expiredCount, expiringSoonCount };
  }, [identities]);

  // Filtered identities
  const filteredIdentities = useMemo(() => {
    return identities.filter((i) => {
      // Trash filter
      if (activeFilter === 'trash') {
        return i.status === 'TRASHED';
      }
      if (i.status === 'TRASHED') return false;

      // Sidebar route filters
      if (activeFilter === 'favorites' && !i.is_favorite) return false;
      if (activeFilter === 'personal') {
        const cat = (i.category || '').toLowerCase();
        const type = (i.identityType || '').toLowerCase();
        if (cat !== 'personal' && type !== 'personal') return false;
      }
      if (activeFilter === 'family') {
        const cat = (i.category || '').toLowerCase();
        const type = (i.identityType || '').toLowerCase();
        if (cat !== 'family' && type !== 'family_member') return false;
      }
      if (activeFilter === 'work') {
        const cat = (i.category || '').toLowerCase();
        const type = (i.identityType || '').toLowerCase();
        if (cat !== 'work' && type !== 'work_id') return false;
      }
      if (activeFilter === 'shared-with-me') {
        const userEmail = (user?.email || '').toLowerCase();
        const isShared = Array.isArray(i.shares) && i.shares.some((s) => s.email === userEmail);
        if (!isShared) return false;
      }
      if (activeFilter === 'shared-by-me') {
        if (!Array.isArray(i.shares) || i.shares.length === 0) return false;
      }
      if (activeFilter === 'documents') {
        if (!Array.isArray(i.attachments) || i.attachments.length === 0) return false;
      }

      // Dropdown Type filter
      if (typeFilter !== 'all') {
        if (i.identityType !== typeFilter) return false;
      }

      // Dropdown Category filter
      if (categoryFilter !== 'all') {
        if ((i.category || '').toLowerCase() !== categoryFilter.toLowerCase()) return false;
      }

      // Dropdown Expiry filter
      if (expiryFilter !== 'all') {
        const f = i.fields || i;
        const info = getExpiryDetails(f.expiryDate);
        if (expiryFilter === 'expired' && info.status !== 'expired') return false;
        if (expiryFilter === 'expiring_soon' && info.status !== 'expiring_soon') return false;
        if (expiryFilter === 'valid' && (info.status === 'expired' || info.status === 'expiring_soon')) return false;
      }

      // Search Query filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const f = i.fields || i;
        const titleMatch = (i.title || '').toLowerCase().includes(q);
        const nameMatch = (f.fullName || '').toLowerCase().includes(q);
        const docNumMatch = (f.documentNumber || '').toLowerCase().includes(q);
        const natMatch = (f.nationality || '').toLowerCase().includes(q);
        const authMatch = (f.issuingAuthority || '').toLowerCase().includes(q);
        const catMatch = (i.category || '').toLowerCase().includes(q);
        const tagMatch = Array.isArray(i.tags) && i.tags.some((t) => t.toLowerCase().includes(q));

        if (!titleMatch && !nameMatch && !docNumMatch && !natMatch && !authMatch && !catMatch && !tagMatch) {
          return false;
        }
      }

      return true;
    });
  }, [identities, activeFilter, typeFilter, categoryFilter, expiryFilter, searchQuery, user]);

  // Sorted identities
  const sortedIdentities = useMemo(() => {
    const list = [...filteredIdentities];
    return list.sort((a, b) => {
      if (sortBy === 'title_asc') {
        return (a.title || '').localeCompare(b.title || '');
      }
      if (sortBy === 'title_desc') {
        return (b.title || '').localeCompare(a.title || '');
      }
      if (sortBy === 'created_desc') {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      if (sortBy === 'expiry_soonest') {
        const fa = a.fields || a;
        const fb = b.fields || b;
        const infoA = getExpiryDetails(fa.expiryDate);
        const infoB = getExpiryDetails(fb.expiryDate);
        return infoA.daysRemaining - infoB.daysRemaining;
      }
      // default: updated_desc
      return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
    });
  }, [filteredIdentities, sortBy]);

  // All extracted documents across all identities for the Documents view
  const allDocumentsList = useMemo(() => {
    const docs = [];
    identities.forEach((i) => {
      if (i.status === 'TRASHED') return;
      if (Array.isArray(i.attachments)) {
        i.attachments.forEach((att) => {
          docs.push({
            ...att,
            identityId: i.id,
            identityTitle: i.title,
            identityType: i.identityType,
          });
        });
      }
    });
    return docs;
  }, [identities]);

  // Header Title & Meta according to active filter
  const getHeaderInfo = () => {
    switch (activeFilter) {
      case 'favorites':
        return {
          title: 'Favorite Identities',
          subtitle: 'Starred and prioritized identity cards & official documents.',
          icon: Star,
          color: '#f59e0b',
        };
      case 'personal':
        return {
          title: 'Personal Identities',
          subtitle: 'Passports, national IDs, driver licenses, and personal certificates.',
          icon: User,
          color: '#8b5cf6',
        };
      case 'family':
        return {
          title: 'Family Identities',
          subtitle: 'Credentials, health cards, and records for family members and dependents.',
          icon: Users,
          color: '#ec4899',
        };
      case 'work':
        return {
          title: 'Work & Enterprise Identities',
          subtitle: 'Employee badges, professional credentials, and security clearance IDs.',
          icon: Briefcase,
          color: '#3b82f6',
        };
      case 'shared-with-me':
        return {
          title: 'Shared With Me',
          subtitle: 'Identities and document scans shared with you by verified collaborators.',
          icon: Users,
          color: '#06b6d4',
        };
      case 'shared-by-me':
        return {
          title: 'Shared By Me',
          subtitle: 'Identity records you have securely shared with external users or team members.',
          icon: Network,
          color: '#10b981',
        };
      case 'documents':
        return {
          title: 'Identity Document Scans',
          subtitle: 'Centralized gallery of all encrypted passport, license, and certificate scans.',
          icon: Paperclip,
          color: '#6366f1',
        };
      case 'categories':
        return {
          title: 'Identity Categories',
          subtitle: 'Organize and structure identities across Personal, Family, Work, and Legal domains.',
          icon: Folder,
          color: '#f59e0b',
        };
      case 'trash':
        return {
          title: 'Identity Trash',
          subtitle: 'Deleted identities preserved securely. You can restore or permanently erase them.',
          icon: Trash2,
          color: '#ef4444',
        };
      default:
        return {
          title: 'All Identities',
          subtitle: 'Identity manager, government credentials & document scans.',
          icon: Shield,
          color: '#6366f1',
        };
    }
  };

  const headerInfo = getHeaderInfo();
  const HeaderIcon = headerInfo.icon;

  // Mask string representation helper
  const getMaskedNumber = (val) => {
    if (!val) return '—';
    if (val.length <= 4) return '••••';
    const last4 = val.slice(-4);
    return `•••• •••• ${last4}`;
  };

  return (
    <MainLayout>
      {/* Toast notice */}
      {toastMessage && (
        <div className="notes-toast animate-slide-down">
          <CheckCircle2 size={16} color="#10b981" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Top Header Banner - Full Bleed Edge-to-Edge with Multicolor Gradient */}
      <div className="notes-module-header notes-hero-banner">
        <div className="notes-hero-content">
          <div className="notes-header-left">
            <div
              className="notes-app-icon-badge notes-hero-icon-badge"
              style={{ background: 'rgba(255, 255, 255, 0.2)', color: '#ffffff' }}
            >
              <HeaderIcon size={24} />
            </div>
            <div>
              <div className="notes-header-title-row">
                <h1 className="notes-page-title notes-hero-title">{headerInfo.title}</h1>
              </div>
              <p className="notes-page-subtitle notes-hero-subtitle">{headerInfo.subtitle}</p>
            </div>
          </div>

          <div className="notes-header-actions">
            {activeFilter === 'trash' ? (
              <button
                type="button"
                className="btn btn-danger notes-hero-btn-danger"
                onClick={handleEmptyTrash}
                disabled={filteredIdentities.length === 0}
              >
                <Trash2 size={15} />
                <span>Empty Trash</span>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-secondary notes-hero-btn-secondary"
                  onClick={() => setTemplatesModalOpen(true)}
                  title="Choose from 10 ready-made identity templates"
                >
                  <Sparkles size={16} color="#fde047" />
                  <span>Templates</span>
                </button>

                {canCreate && (
                  <button
                    type="button"
                    className="btn btn-primary notes-hero-btn-primary"
                    onClick={() => {
                      setEditingIdentity(null);
                      setIsEditorOpen(true);
                    }}
                  >
                    <Plus size={16} />
                    <span>Add Identity</span>
                  </button>
                )}
              </>
            )}

            <button
              type="button"
              className="btn-icon notes-refresh-btn notes-hero-btn-secondary"
              onClick={loadIdentities}
              title="Refresh and sync identities"
            >
              <RefreshCw size={17} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      <div className="notes-module-container identity-module-container">

        {/* Expiry Alert Banner (if any active item is expired or expiring within 30 days) */}
        {activeFilter !== 'trash' && (expiryStats.expiredCount > 0 || expiryStats.expiringSoonCount > 0) && (
          <div className="identity-expiry-alert-banner">
            <div className="identity-expiry-alert-left">
              <AlertTriangle size={20} className="text-warning" />
              <div>
                <strong>Identity Document Validity Reminders</strong>
                <p>
                  {expiryStats.expiredCount > 0 && `${expiryStats.expiredCount} document has expired. `}
                  {expiryStats.expiringSoonCount > 0 &&
                    `${expiryStats.expiringSoonCount} document(s) expire within 30 days. `}
                  Please review renewal or replacement requirements.
                </p>
              </div>
            </div>
            <div className="identity-expiry-alert-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setExpiryFilter(expiryStats.expiredCount > 0 ? 'expired' : 'expiring_soon')}
              >
                View Expiring
              </button>
            </div>
          </div>
        )}

        {/* Main Workspace */}
        {activeFilter === 'documents' ? (
          /* Documents Gallery View */
          <div className="identity-documents-gallery-section">
            <div className="identity-docs-gallery-header">
              <h3>All Attached Document Scans ({allDocumentsList.length})</h3>
              <p className="text-secondary text-sm">
                Encrypted scanned attachments extracted across all identity records in your vault.
              </p>
            </div>

            {allDocumentsList.length === 0 ? (
              <div className="notes-empty-state glass-panel">
                <Paperclip size={48} opacity={0.3} />
                <h3>No Attached Document Scans</h3>
                <p>Attach passport copies, driver licenses, or certificates to your identity records.</p>
              </div>
            ) : (
              <div className="identity-attachments-grid" style={{ marginTop: '16px' }}>
                {allDocumentsList.map((doc) => {
                  const isImage = doc.type?.startsWith('image/');
                  const sizeKb = (doc.size / 1024).toFixed(1);
                  return (
                    <div key={doc.id} className="identity-attachment-card">
                      <div className="identity-attachment-preview">
                        {isImage && doc.dataUrl ? (
                          <img src={doc.dataUrl} alt={doc.name} className="identity-attachment-thumb" />
                        ) : (
                          <Shield size={36} className="text-primary" />
                        )}
                      </div>
                      <div className="identity-attachment-info">
                        <span className="identity-attachment-name" title={doc.name}>
                          {doc.name}
                        </span>
                        <span className="identity-attachment-meta">
                          Linked to: <strong>{doc.identityTitle}</strong>
                        </span>
                        <span className="text-secondary text-sm">
                          {sizeKb} KB • {new Date(doc.uploadedAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="identity-attachment-actions">
                        {doc.dataUrl && (
                          <a
                            href={doc.dataUrl}
                            download={doc.name}
                            className="btn-icon btn-sm"
                            title="Download decrypted scan"
                          >
                            <Download size={14} />
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : activeFilter === 'categories' ? (
          /* Categories View */
          <div className="identity-categories-view">
            <div className="identity-categories-header">
              <h3>Identity Categories</h3>
              <p className="text-secondary text-sm">
                Browse identities grouped by classification domains.
              </p>
            </div>

            <div className="identity-categories-grid">
              {categories.map((cat) => {
                const count = identities.filter(
                  (i) => i.status !== 'TRASHED' && (i.category || '').toLowerCase() === cat.name.toLowerCase()
                ).length;
                return (
                  <div
                    key={cat.id || cat.name}
                    className="identity-category-card glass-panel"
                    onClick={() => {
                      setCategoryFilter(cat.name);
                      navigate(`/apps/identities?category=${encodeURIComponent(cat.name)}`);
                    }}
                  >
                    <div className="identity-cat-icon">{cat.icon || '📁'}</div>
                    <div className="identity-cat-info">
                      <h4>{cat.name}</h4>
                      <span className="text-secondary text-sm">{count} Identities</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* Normal Dashboard / Favorites / Type / Trash View */
          <>
            {/* Search, Filters & View Mode Toolbar */}
            <div className="notes-toolbar identities-desktop-toolbar">
              <div className="notes-toolbar-left">
                <div className="notes-search-box">
                  <Search size={16} className="search-icon" />
                  <input
                    type="text"
                    className="notes-search-input"
                    placeholder="Search by title, legal name, doc number, tag..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="notes-search-clear"
                      onClick={() => setSearchQuery('')}
                    >
                      &times;
                    </button>
                  )}
                </div>

                {/* Identity Type Filter */}
                <select
                  className="notes-select"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="all">All Types</option>
                  <option value="personal">Personal Identity</option>
                  <option value="government_id">Government ID / SSN</option>
                  <option value="passport">Passport</option>
                  <option value="driving_licence">Driving Licence</option>
                  <option value="health_id">Health / Insurance ID</option>
                  <option value="work_id">Work Identity</option>
                  <option value="student_id">Student Identity</option>
                  <option value="family_member">Family Member</option>
                  <option value="address_id">Address Identity</option>
                  <option value="custom">Custom Identity</option>
                </select>

                {/* Category Filter */}
                <select
                  className="notes-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  <option value="Personal">Personal</option>
                  <option value="Family">Family</option>
                  <option value="Work">Work</option>
                  <option value="Legal">Legal</option>
                  <option value="Medical">Medical</option>
                  {categories
                    .filter((c) => !['Personal', 'Family', 'Work', 'Legal', 'Medical'].includes(c.name))
                    .map((c) => (
                      <option key={c.id || c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                </select>

                {/* Expiry Filter */}
                <select
                  className="notes-select"
                  value={expiryFilter}
                  onChange={(e) => setExpiryFilter(e.target.value)}
                >
                  <option value="all">All Expiry States</option>
                  <option value="valid">Valid Documents</option>
                  <option value="expiring_soon">Expiring Soon (≤30d)</option>
                  <option value="expired">Expired Documents</option>
                </select>
              </div>

              <div className="notes-toolbar-right">
                {/* Sort dropdown */}
                <select
                  className="notes-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="updated_desc">Recently Updated</option>
                  <option value="title_asc">Title (A - Z)</option>
                  <option value="title_desc">Title (Z - A)</option>
                  <option value="expiry_soonest">Expiry: Soonest First</option>
                  <option value="created_desc">Creation Date</option>
                </select>

                {/* Grid / List View Toggle */}
                <div className="notes-view-mode-switch">
                  <button
                    type="button"
                    className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => toggleViewMode('grid')}
                    title="Grid View"
                  >
                    <LayoutGrid size={16} />
                  </button>
                  <button
                    type="button"
                    className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => toggleViewMode('list')}
                    title="List View"
                  >
                    <ListIcon size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Single-Row Filter Bar */}
            <div className="identities-mobile-filter-bar">
              {/* Search Toggle Icon Button */}
              <button
                type="button"
                className={`identities-mobile-icon-btn ${searchQuery || isMobileSearchOpen ? 'is-active' : ''}`}
                onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
                title="Search identities"
                aria-label="Search identities"
              >
                <Search size={17} />
                {searchQuery && <span className="identities-filter-dot" />}
              </button>

              {/* Type Filter Icon Button */}
              <div
                className={`identities-mobile-icon-btn ${typeFilter !== 'all' ? 'is-active' : ''}`}
                title="Filter by document type"
              >
                <Shield size={17} />
                {typeFilter !== 'all' && <span className="identities-filter-dot" />}
                <select
                  className="identities-filter-hidden-select"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  aria-label="Filter by type"
                >
                  <option value="all">All Types</option>
                  <option value="personal">Personal Identity</option>
                  <option value="government_id">Government ID / SSN</option>
                  <option value="passport">Passport</option>
                  <option value="driving_licence">Driving Licence</option>
                  <option value="health_id">Health / Insurance ID</option>
                  <option value="work_id">Work Identity</option>
                  <option value="student_id">Student Identity</option>
                  <option value="family_member">Family Member</option>
                  <option value="address_id">Address Identity</option>
                  <option value="custom">Custom Identity</option>
                </select>
              </div>

              {/* Category Filter Icon Button */}
              <div
                className={`identities-mobile-icon-btn ${categoryFilter !== 'all' ? 'is-active' : ''}`}
                title="Filter by category"
              >
                <Folder size={17} />
                {categoryFilter !== 'all' && <span className="identities-filter-dot" />}
                <select
                  className="identities-filter-hidden-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  aria-label="Filter by category"
                >
                  <option value="all">All Categories</option>
                  <option value="Personal">Personal</option>
                  <option value="Family">Family</option>
                  <option value="Work">Work</option>
                  <option value="Legal">Legal</option>
                  <option value="Medical">Medical</option>
                  {categories
                    .filter((c) => !['Personal', 'Family', 'Work', 'Legal', 'Medical'].includes(c.name))
                    .map((c) => (
                      <option key={c.id || c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Expiry Filter Icon Button */}
              <div
                className={`identities-mobile-icon-btn ${expiryFilter !== 'all' ? 'is-active' : ''}`}
                title="Filter by validity & expiry"
              >
                <Clock size={17} />
                {expiryFilter !== 'all' && <span className="identities-filter-dot" />}
                <select
                  className="identities-filter-hidden-select"
                  value={expiryFilter}
                  onChange={(e) => setExpiryFilter(e.target.value)}
                  aria-label="Filter by validity & expiry"
                >
                  <option value="all">All Expiry States</option>
                  <option value="valid">Valid Documents</option>
                  <option value="expiring_soon">Expiring Soon (≤30d)</option>
                  <option value="expired">Expired Documents</option>
                </select>
              </div>

              {/* Sort Icon Button */}
              <div
                className={`identities-mobile-icon-btn ${sortBy !== 'updated_desc' ? 'is-active' : ''}`}
                title="Sort identities"
              >
                <ArrowUpDown size={17} />
                {sortBy !== 'updated_desc' && <span className="identities-filter-dot" />}
                <select
                  className="identities-filter-hidden-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Sort identities"
                >
                  <option value="updated_desc">Recently Updated</option>
                  <option value="title_asc">Title (A - Z)</option>
                  <option value="title_desc">Title (Z - A)</option>
                  <option value="expiry_soonest">Expiry: Soonest First</option>
                  <option value="created_desc">Creation Date</option>
                </select>
              </div>

              {/* Grid / List View Toggle */}
              <div className="identities-mobile-view-toggle">
                <button
                  type="button"
                  className={viewMode === 'grid' ? 'active' : ''}
                  onClick={() => toggleViewMode('grid')}
                  aria-label="Grid view"
                >
                  <LayoutGrid size={16} />
                </button>
                <button
                  type="button"
                  className={viewMode === 'list' ? 'active' : ''}
                  onClick={() => toggleViewMode('list')}
                  aria-label="List view"
                >
                  <ListIcon size={16} />
                </button>
              </div>

              {/* Reset if active */}
              {(searchQuery || typeFilter !== 'all' || categoryFilter !== 'all' || expiryFilter !== 'all' || sortBy !== 'updated_desc') && (
                <button
                  type="button"
                  className="identities-mobile-icon-btn identities-mobile-reset-btn"
                  onClick={() => {
                    setSearchQuery('');
                    setTypeFilter('all');
                    setCategoryFilter('all');
                    setExpiryFilter('all');
                    setSortBy('updated_desc');
                  }}
                  title="Reset all filters"
                  aria-label="Reset all filters"
                >
                  <X size={16} />
                </button>
              )}

              {/* Reload / Refresh Button */}
              <button
                type="button"
                className="identities-mobile-icon-btn identities-mobile-refresh-btn"
                onClick={loadIdentities}
                title="Reload identities"
                aria-label="Reload identities"
              >
                <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>

            {/* Expandable Mobile Search Bar */}
            {isMobileSearchOpen && (
              <div className="identities-mobile-search-expand">
                <Search size={16} className="identities-mobile-search-inner-icon" />
                <input
                  type="text"
                  className="identities-mobile-search-input"
                  placeholder="Search by title, legal name, doc number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                {searchQuery ? (
                  <button
                    type="button"
                    className="identities-mobile-search-clear"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <X size={15} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="identities-mobile-search-clear"
                    onClick={() => setIsMobileSearchOpen(false)}
                    aria-label="Close search"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>
            )}

            {/* Content Display */}
            {loading ? (
              <div className="notes-loading-state">
                <RefreshCw size={28} className="animate-spin text-primary" />
                <p>Decrypting identities with client-side zero-knowledge AES-256...</p>
              </div>
            ) : loadError ? (
              <div className="alert alert-danger" style={{ margin: '20px 0' }}>
                <AlertCircle size={16} />
                <span>{loadError}</span>
              </div>
            ) : sortedIdentities.length === 0 ? (
              <div className="notes-empty-state glass-panel">
                <Shield size={56} opacity={0.3} />
                <h3>No Identities Found</h3>
                <p>
                  {searchQuery || typeFilter !== 'all' || categoryFilter !== 'all' || expiryFilter !== 'all'
                    ? 'No identity records match your current filters.'
                    : activeFilter === 'trash'
                    ? 'The trash is currently empty.'
                    : 'Start storing passports, driver licenses, and official cards in your zero-knowledge vault.'}
                </p>
                {activeFilter !== 'trash' && canCreate && (
                  <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setTemplatesModalOpen(true)}
                    >
                      <Sparkles size={16} />
                      <span>Use Template</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setEditingIdentity(null);
                        setIsEditorOpen(true);
                      }}
                    >
                      <Plus size={16} />
                      <span>Create Identity</span>
                    </button>
                  </div>
                )}
              </div>
            ) : viewMode === 'grid' ? (
              /* Grid View */
              <div className="identity-cards-grid">
                {sortedIdentities.map((item) => {
                  const f = item.fields || item;
                  const IconComp = IDENTITY_TYPE_ICONS[item.identityType] || User;
                  const expiryInfo = getExpiryDetails(f.expiryDate);
                  const isRevealed = Boolean(revealedIds[item.id]);
                  const docCount = Array.isArray(item.attachments) ? item.attachments.length : 0;
                  const shareCount = Array.isArray(item.shares) ? item.shares.length : 0;

                  return (
                    <div
                      key={item.id}
                      className={`identity-card glass-panel ${item.is_favorite ? 'is-favorite' : ''}`}
                    >
                      {/* Card Top: Type badge, Favorite, Actions Menu */}
                      <div className="identity-card-header">
                        <div className="identity-card-badge-group">
                          <div className="identity-card-icon-box">
                            <IconComp size={18} />
                          </div>
                          <div>
                            <span className="badge badge-secondary">
                              {IDENTITY_TYPE_NAMES[item.identityType] || 'Identity'}
                            </span>
                            <span className="badge badge-secondary" style={{ marginLeft: '6px' }}>
                              {item.category || 'Personal'}
                            </span>
                          </div>
                        </div>

                        <div className="identity-card-top-actions">
                          {activeFilter !== 'trash' && (
                            <button
                              type="button"
                              className={`btn-icon ${item.is_favorite ? 'text-warning' : ''}`}
                              onClick={() => handleToggleFavorite(item)}
                              title={item.is_favorite ? 'Remove Favorite' : 'Mark Favorite'}
                            >
                              <Star size={16} fill={item.is_favorite ? 'currentColor' : 'none'} />
                            </button>
                          )}

                          {/* Three-dot dropdown */}
                          <div className="notes-card-menu-relative identity-card-menu-anchor">
                            <button
                              type="button"
                              className="btn-icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveMenuId(activeMenuId === item.id ? null : item.id);
                              }}
                            >
                              <MoreVertical size={16} />
                            </button>

                            {activeMenuId === item.id && (
                              <>
                                <div
                                  className="identity-menu-backdrop"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveMenuId(null);
                                  }}
                                />
                                <div
                                  className="notes-dropdown-menu animate-scale-in"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="identity-menu-header">
                                    <div className="identity-menu-title">{item.title || 'Untitled Identity'}</div>
                                    <div className="identity-menu-sub">{item.category || 'Personal'} • {IDENTITY_TYPE_NAMES[item.identityType] || 'Identity'}</div>
                                  </div>
                                  <div className="identity-menu-header-divider" />

                                  {activeFilter === 'trash' ? (
                                    <>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => {
                                          setActiveMenuId(null);
                                          handleRestoreFromTrash(item);
                                        }}
                                      >
                                        <RotateCcw size={15} />
                                        <span>Restore Identity</span>
                                      </button>
                                      <button
                                        type="button"
                                        className="dropdown-item text-danger"
                                        onClick={() => {
                                          setActiveMenuId(null);
                                          setDeleteTarget({ identity: item, isPermanent: true });
                                        }}
                                      >
                                        <Trash2 size={15} />
                                        <span>Erase Forever</span>
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => {
                                          setActiveMenuId(null);
                                          setViewingIdentity(item);
                                        }}
                                      >
                                        <Eye size={15} />
                                        <span>Open Details</span>
                                      </button>
                                      {canEdit && (
                                        <button
                                          type="button"
                                          className="dropdown-item"
                                          onClick={() => {
                                            setActiveMenuId(null);
                                            setEditingIdentity(item);
                                            setIsEditorOpen(true);
                                          }}
                                        >
                                          <Edit3 size={15} />
                                          <span>Edit Identity</span>
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => {
                                          setActiveMenuId(null);
                                          toggleRevealCard(item.id);
                                        }}
                                      >
                                        {isRevealed ? <EyeOff size={15} /> : <Eye size={15} />}
                                        <span>{isRevealed ? 'Hide Sensitive Data' : 'Show Sensitive Data'}</span>
                                      </button>
                                      {f.documentNumber && (
                                        <button
                                          type="button"
                                          className="dropdown-item"
                                          onClick={() => {
                                            setActiveMenuId(null);
                                            copyTextToClipboard(f.documentNumber);
                                            showToast('Document number copied');
                                          }}
                                        >
                                          <Copy size={15} />
                                          <span>Copy Document Number</span>
                                        </button>
                                      )}
                                      {f.fullName && (
                                        <button
                                          type="button"
                                          className="dropdown-item"
                                          onClick={() => {
                                            setActiveMenuId(null);
                                            copyTextToClipboard(f.fullName);
                                            showToast('Full name copied');
                                          }}
                                        >
                                          <Copy size={15} />
                                          <span>Copy Full Name</span>
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => {
                                          setActiveMenuId(null);
                                          handleToggleFavorite(item);
                                        }}
                                      >
                                        <Star size={15} />
                                        <span>{item.is_favorite ? 'Remove Favorite' : 'Add to Favorites'}</span>
                                      </button>
                                      {canCreate && (
                                        <button
                                          type="button"
                                          className="dropdown-item"
                                          onClick={() => {
                                            setActiveMenuId(null);
                                            handleDuplicateIdentity(item);
                                          }}
                                        >
                                          <Copy size={15} />
                                          <span>Duplicate</span>
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => {
                                          setActiveMenuId(null);
                                          setMoveToCategoryTarget(item);
                                        }}
                                      >
                                        <Folder size={15} />
                                        <span>Move to Category</span>
                                      </button>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => {
                                          setActiveMenuId(null);
                                          setManageTagsTarget(item);
                                        }}
                                      >
                                        <Tag size={15} />
                                        <span>Manage Tags</span>
                                      </button>
                                      {canEdit && (
                                        <button
                                          type="button"
                                          className="dropdown-item"
                                          onClick={() => {
                                            setActiveMenuId(null);
                                            setEditingIdentity(item);
                                            setIsEditorOpen(true);
                                          }}
                                        >
                                          <Paperclip size={15} />
                                          <span>Attach Document Scan</span>
                                        </button>
                                      )}
                                      {canShare && (
                                        <button
                                          type="button"
                                          className="dropdown-item"
                                          onClick={() => {
                                            setActiveMenuId(null);
                                            setShareIdentityTarget(item);
                                          }}
                                        >
                                          <Share2 size={15} />
                                          <span>Share Identity</span>
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => {
                                          setActiveMenuId(null);
                                          handleCopyLink(item);
                                        }}
                                      >
                                        <ExternalLink size={15} />
                                        <span>Copy Link</span>
                                      </button>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => {
                                          setActiveMenuId(null);
                                          setActivityIdentity(item);
                                        }}
                                      >
                                        <History size={15} />
                                        <span>View Activity</span>
                                      </button>
                                      {canDelete && (
                                        <button
                                          type="button"
                                          className="dropdown-item text-danger"
                                          onClick={() => {
                                            setActiveMenuId(null);
                                            setDeleteTarget({ identity: item, isPermanent: false });
                                          }}
                                        >
                                          <Trash2 size={15} />
                                          <span>Move to Trash</span>
                                        </button>
                                      )}
                                    </>
                                  )}

                                  <div className="identity-menu-cancel-row">
                                    <button
                                      type="button"
                                      className="identity-menu-cancel-btn"
                                      onClick={() => setActiveMenuId(null)}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Card Body */}
                      <div
                        className="identity-card-body"
                        onClick={() => setViewingIdentity(item)}
                        style={{ cursor: 'pointer' }}
                      >
                        <h3 className="identity-card-title">{item.title}</h3>
                        <p className="identity-card-name">
                          <strong>{f.fullName || 'No Legal Name Stored'}</strong>
                        </p>

                        {/* Sensitive Document Box */}
                        <div
                          className="identity-card-sensitive-box"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="identity-card-num-text">
                            {isRevealed
                              ? f.documentNumber || '—'
                              : getMaskedNumber(f.documentNumber)}
                          </div>
                          <div className="identity-card-num-actions">
                            {f.documentNumber && (
                              <>
                                <button
                                  type="button"
                                  className="btn-icon btn-sm"
                                  onClick={() => toggleRevealCard(item.id)}
                                  title={isRevealed ? 'Hide (auto-hides in 15s)' : 'Reveal for 15s'}
                                >
                                  {isRevealed ? <EyeOff size={14} /> : <Eye size={14} />}
                                </button>
                                <button
                                  type="button"
                                  className="btn-icon btn-sm"
                                  onClick={() => {
                                    copyTextToClipboard(f.documentNumber);
                                    setCopiedId(item.id);
                                    setTimeout(() => setCopiedId(null), 2000);
                                  }}
                                  title="Copy document number"
                                >
                                  {copiedId === item.id ? (
                                    <Check size={14} className="text-success" />
                                  ) : (
                                    <Copy size={14} />
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Expiry Pill */}
                        {expiryInfo.status !== 'none' && (
                          <div className="identity-card-expiry-row">
                            <span
                              className="identity-expiry-status-pill"
                              style={{
                                backgroundColor: `${expiryInfo.color}15`,
                                color: expiryInfo.color,
                                borderColor: `${expiryInfo.color}35`,
                              }}
                            >
                              <Clock size={12} />
                              {expiryInfo.label}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Card Footer: tags, attachments count, updated date */}
                      <div className="identity-card-footer">
                        <div className="identity-card-footer-meta">
                          {docCount > 0 && (
                            <span className="meta-item" title={`${docCount} document scan(s) attached`}>
                              <Paperclip size={13} />
                              <span>{docCount}</span>
                            </span>
                          )}
                          {shareCount > 0 && (
                            <span className="meta-item" title={`Shared with ${shareCount} user(s)`}>
                              <Share2 size={13} />
                              <span>{shareCount}</span>
                            </span>
                          )}
                          {item.isLocked && (
                            <span className="meta-item" title="PIN Protected">
                              <Lock size={13} className="text-warning" />
                            </span>
                          )}
                        </div>

                        <span className="identity-card-date">
                          {new Date(item.updated_at || item.created_at).toLocaleDateString([], {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* List View */
              <div className="identity-list-container glass-panel">
                <table className="identity-table">
                  <thead>
                    <tr>
                      <th>Identity</th>
                      <th>Legal Name</th>
                      <th>Document Number</th>
                      <th>Category</th>
                      <th>Expiry</th>
                      <th>Docs</th>
                      <th>Last Updated</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedIdentities.map((item) => {
                      const f = item.fields || item;
                      const IconComp = IDENTITY_TYPE_ICONS[item.identityType] || User;
                      const expiryInfo = getExpiryDetails(f.expiryDate);
                      const isRevealed = Boolean(revealedIds[item.id]);
                      const docCount = Array.isArray(item.attachments) ? item.attachments.length : 0;

                      return (
                        <tr key={item.id} onClick={() => setViewingIdentity(item)}>
                          <td>
                            <div className="table-identity-cell">
                              <div className="table-icon-box">
                                <IconComp size={16} />
                              </div>
                              <div>
                                <div className="table-title">{item.title}</div>
                                <div className="table-type-sub">
                                  {IDENTITY_TYPE_NAMES[item.identityType] || 'Identity'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>{f.fullName || '—'}</td>
                          <td>
                            <div
                              className="table-docnum-cell"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span>
                                {isRevealed ? f.documentNumber || '—' : getMaskedNumber(f.documentNumber)}
                              </span>
                              {f.documentNumber && (
                                <button
                                  type="button"
                                  className="btn-icon btn-sm"
                                  onClick={() => toggleRevealCard(item.id)}
                                  title={isRevealed ? 'Hide' : 'Reveal for 15s'}
                                >
                                  {isRevealed ? <EyeOff size={13} /> : <Eye size={13} />}
                                </button>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="badge badge-secondary">{item.category || 'Personal'}</span>
                          </td>
                          <td>
                            {expiryInfo.status !== 'none' ? (
                              <span
                                className="identity-expiry-status-pill"
                                style={{
                                  backgroundColor: `${expiryInfo.color}15`,
                                  color: expiryInfo.color,
                                  borderColor: `${expiryInfo.color}35`,
                                }}
                              >
                                {expiryInfo.label}
                              </span>
                            ) : (
                              <span className="text-secondary text-sm">—</span>
                            )}
                          </td>
                          <td>
                            {docCount > 0 ? (
                              <span className="meta-item">
                                <Paperclip size={13} />
                                <span>{docCount}</span>
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td>
                            {new Date(item.updated_at || item.created_at).toLocaleDateString([], {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </td>
                          <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'inline-flex', gap: '6px' }}>
                              <button
                                type="button"
                                className="btn-icon btn-sm"
                                onClick={() => setViewingIdentity(item)}
                                title="View Details"
                              >
                                <Eye size={15} />
                              </button>
                              {canEdit && (
                                <button
                                  type="button"
                                  className="btn-icon btn-sm"
                                  onClick={() => {
                                    setEditingIdentity(item);
                                    setIsEditorOpen(true);
                                  }}
                                  title="Edit Identity"
                                >
                                  <Edit3 size={15} />
                                </button>
                              )}
                              <button
                                type="button"
                                className={`btn-icon btn-sm ${item.is_favorite ? 'text-warning' : ''}`}
                                onClick={() => handleToggleFavorite(item)}
                                title={item.is_favorite ? 'Remove Favorite' : 'Mark Favorite'}
                              >
                                <Star size={15} fill={item.is_favorite ? 'currentColor' : 'none'} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ───────────────────────────────────────────── */}
        {/* Modals */}
        {/* ───────────────────────────────────────────── */}

        {/* Templates Picker Modal */}
        <IdentityTemplatesModal
          open={templatesModalOpen}
          onClose={() => setTemplatesModalOpen(false)}
          onSelectTemplate={(template) => {
            setTemplatesModalOpen(false);
            setEditingIdentity({
              title: template.title || template.defaultTitle || '',
              identityType: template.identityType || 'personal',
              category: template.category || 'Personal',
              tags: template.tags || [],
              fields: { ...template.fields },
              attachments: [],
              isLocked: false,
              lockPin: '',
              shares: [],
            });
            setIsEditorOpen(true);
          }}
        />

        {/* Identity Editor Modal */}
        <IdentityEditorModal
          open={isEditorOpen}
          identity={editingIdentity}
          categories={categories}
          onClose={() => {
            setIsEditorOpen(false);
            setEditingIdentity(null);
          }}
          onSave={handleSaveIdentity}
          hasEditPermission={canEdit}
          onOpenTemplates={() => {
            setIsEditorOpen(false);
            setTemplatesModalOpen(true);
          }}
        />

        {/* Identity Viewer Modal */}
        <IdentityViewModal
          open={Boolean(viewingIdentity)}
          identity={viewingIdentity}
          onClose={() => setViewingIdentity(null)}
          onEdit={(idObj) => {
            setEditingIdentity(idObj);
            setIsEditorOpen(true);
          }}
          onToggleFavorite={handleToggleFavorite}
          onDelete={(idObj) => {
            setViewingIdentity(null);
            setDeleteTarget({ identity: idObj, isPermanent: false });
          }}
          onShare={(idObj) => {
            setShareIdentityTarget(idObj);
          }}
          hasEditPermission={canEdit}
        />

        {/* Activity & Version History Modal */}
        <IdentityActivityModal
          open={Boolean(activityIdentity)}
          identity={activityIdentity}
          onClose={() => setActivityIdentity(null)}
          onRestoreVersion={handleRestoreVersion}
        />

        {/* Move to Category Modal */}
        <MoveIdentityCategoryModal
          open={Boolean(moveToCategoryTarget)}
          identity={moveToCategoryTarget}
          categories={categories}
          onClose={() => setMoveToCategoryTarget(null)}
          onSave={handleMoveToCategory}
        />

        {/* Manage Tags Modal */}
        <ManageIdentityTagsModal
          open={Boolean(manageTagsTarget)}
          identity={manageTagsTarget}
          onClose={() => setManageTagsTarget(null)}
          onSave={handleSaveTags}
        />

        {/* Delete Identity Modal (Soft delete or permanent purge) */}
        <DeleteIdentityModal
          open={Boolean(deleteTarget)}
          identity={deleteTarget?.identity}
          isPermanent={deleteTarget?.isPermanent}
          onClose={() => setDeleteTarget(null)}
          onConfirm={(target) => {
            if (deleteTarget?.isPermanent) {
              handlePermanentDelete(target);
            } else {
              handleMoveToTrash(target);
            }
          }}
        />

        {/* Standard CipherVault Share Modal */}
        {shareIdentityTarget && (
          <ShareVaultItemModal
            isOpen={Boolean(shareIdentityTarget)}
            onClose={() => setShareIdentityTarget(null)}
            item={{
              id: shareIdentityTarget.id,
              name: shareIdentityTarget.title,
            }}
            onShared={() => {
              showToast('Identity shared successfully');
            }}
          />
        )}
      </div>
    </MainLayout>
  );
}
